import React, { useState } from 'react';
import { DroneInfoOverlay } from '@src/features/drone/pages/drones-page/components/drone-info-overlay';
import { DroneEntity } from '@src/core/features';
import { SelectedDroneMarker } from '@src/features/drone/pages/drones-page/components/selected-drone-marker';
import { DroneMarkersList } from '@src/features/drone/pages/drones-page/components/drone-marker-list';
import { useSelector } from 'react-redux';
import { selectHoveredDrone } from '@src/features/drone/redux';
import { DronesMainMapProps } from './types';
import { useCloseOverlayOnDroneChange } from '@src/features/drone/hooks/useCloseOverlayOnDroneChange';
import { ArcPolyline } from '@src/core/controllers';
import { Marker } from '@react-google-maps/api';
import gohere from '@src/assets/gohere.svg';
import { useCameraLayout } from '@src/features/order/components/camera-view/camera-layout-context';
import {
  getFlightHeading,
  getFlightPosition,
} from '@src/features/order/provider/flight-provider/flight-position-cache';
import { useDronesPage } from '@src/features/drone/context/drones-page-view-model';

export const DronesMainMap: React.FC<DronesMainMapProps> = (props) => {
  const { resetGoHere, position, removePosition, points = [] } = props;
  const vm = useDronesPage();
  const [clickedDrone, setClickedDrone] = useState<DroneEntity | null>(null);
  const hoveredDrone = useSelector(selectHoveredDrone);
  const { setCameraSource } = useCameraLayout();

  useCloseOverlayOnDroneChange(
    vm.selectedFromRedux,
    position,
    removePosition,
    clickedDrone,
    setClickedDrone,
  );

  React.useEffect(() => {
    if (!vm.selectedDrone) {
      setCameraSource(null);
      return;
    }
    setCameraSource({
      serialNumber: vm.selectedDrone.serialNumber,
      resetGoHere,
      isEnRoute: vm.isOnMission,
    });
  }, [vm.selectedDrone?.serialNumber, vm.isOnMission, resetGoHere, setCameraSource]);

  const goHereOrigin = React.useMemo(() => {
    if (!vm.selectedDrone) return null;
    const cached = getFlightPosition(vm.selectedDrone.serialNumber);
    if (cached) {
      const lat = Number(cached.latitude);
      const lng = Number(cached.longitude);
      if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
    }
    const lat = Number(vm.selectedDrone.latitude);
    const lng = Number(vm.selectedDrone.longitude);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  }, [vm.selectedDrone, vm.flightData]);

  const handleDroneClick = React.useCallback((drone: DroneEntity) => {
    setClickedDrone(drone);
  }, []);

  const clickedDronePosition = React.useMemo(() => {
    if (!clickedDrone) return null;
    return { lat: Number(clickedDrone.latitude), lng: Number(clickedDrone.longitude) };
  }, [clickedDrone?.latitude, clickedDrone?.longitude]);

  const errorPath = React.useMemo(() => {
    if (!goHereOrigin) return [];
    const targets = points
      .map((p) => p.position)
      .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
    if (targets.length === 0) return [];
    return [goHereOrigin, ...targets];
  }, [goHereOrigin, points]);

  return (
    <React.Fragment>
      {clickedDrone && clickedDronePosition && (
        <DroneInfoOverlay
          drone={clickedDrone}
          position={clickedDronePosition}
          onClose={() => setClickedDrone(null)}
          clickedDrone={clickedDrone}
          setClickedDrone={setClickedDrone}
        />
      )}

      {vm.selectedDrone ? (
        <SelectedDroneMarker
          drone={vm.selectedDrone}
          onClick={() => handleDroneClick(vm.selectedDrone!)}
          rotation={
            vm.rotations[vm.selectedDrone.serialNumber] ??
            getFlightHeading(vm.selectedDrone.serialNumber)
          }
          isOnMission={vm.isOnMission}
        />
      ) : (
        <DroneMarkersList
          idleDrones={vm.idleDrones}
          flyingDrones={vm.flyingDrones}
          clickedDrone={clickedDrone}
          hoveredDrone={hoveredDrone}
          rotations={vm.rotations}
          onDroneClick={handleDroneClick}
        />
      )}

      {errorPath.length >= 2 && <ArcPolyline.error path={errorPath} />}

      {points.map((point) => (
        <Marker
          key={point.id}
          position={point.position}
          icon={{
            url: gohere,
            scaledSize: new window.google.maps.Size(80, 40),
            origin: new window.google.maps.Point(0, 0),
            anchor: new window.google.maps.Point(12, 38),
          }}
        />
      ))}
    </React.Fragment>
  );
};
